import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, BookOpenText, Download, Lock } from "lucide-react";
import { getStudentChapterBook, openChapterBookPdf } from "@/lib/books";

/**
 * Student-facing Reference Book tab. Renders Theory, Practical, Student
 * Notes, Glossary, and Further reading — NEVER teacher delivery notes.
 *
 * Fetches `/api/student/courses/{courseId}/chapters/{chapter_id}/book`.
 * 403/404 → friendly placeholder ("Your teacher hasn't shared this yet").
 */
export default function StudentBookTab({ courseId, chapter }) {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState(null); // "not_shared" | "not_ready" | "payment_required" | null

  useEffect(() => {
    if (!chapter?.id) return;
    setLoading(true);
    setBook(null);
    setReason(null);
    getStudentChapterBook(courseId, chapter.id)
      .then(setBook)
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        if (detail === "book_not_shared") setReason("not_shared");
        else if (detail === "book_not_ready") setReason("not_ready");
        else if (detail === "payment_required") setReason("payment_required");
        else setReason("not_ready");
      })
      .finally(() => setLoading(false));
  }, [courseId, chapter?.id]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-stone-500 text-sm" data-testid="book-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading reference book…
      </div>
    );
  }

  if (!book) {
    const msg =
      reason === "not_shared"
        ? "Your teacher hasn't shared the reference book for this chapter yet."
        : reason === "payment_required"
          ? "Complete your payment to unlock the reference book."
          : "Reference book not available for this chapter yet.";
    return (
      <Card className="border-stone-200" data-testid="book-empty">
        <CardContent className="py-8 text-center">
          <Lock className="mx-auto h-6 w-6 text-stone-400 mb-2" />
          <p className="text-sm text-stone-600">{msg}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="student-book-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BookOpenText className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold">{book.chapter_title}</h2>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => openChapterBookPdf(courseId, chapter.id)}
          data-testid="student-book-pdf-btn"
        >
          <Download className="mr-1 h-4 w-4" /> Download PDF
        </Button>
      </div>

      <Section title="Theory" markdown={book.theory_markdown} />
      <Section title="Practical" markdown={book.practical_markdown} />
      <Section title="Student notes" markdown={book.student_notes_markdown} />

      {book.glossary?.length > 0 && (
        <section data-testid="book-glossary">
          <h3 className="text-sm font-semibold mb-2">Glossary</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-stone-800">
            {book.glossary.map((g, i) => (
              <li key={i}>
                <b>{g.term}</b> — {g.definition}
              </li>
            ))}
          </ul>
        </section>
      )}

      {book.further_reading?.length > 0 && (
        <section data-testid="book-further">
          <h3 className="text-sm font-semibold mb-2">Further reading</h3>
          <ul className="list-disc pl-5 space-y-1 text-sm text-stone-800">
            {book.further_reading.map((r, i) => (
              <li key={i}>
                <b>{r.title}</b>
                {r.url ? (
                  <>
                    {" — "}
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 hover:underline"
                    >
                      {r.url}
                    </a>
                  </>
                ) : null}
                {r.note ? <span className="text-stone-500"> · {r.note}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Section({ title, markdown }) {
  if (!markdown?.trim()) return null;
  return (
    <section>
      <h3 className="text-sm font-semibold mb-2 text-stone-800">{title}</h3>
      <pre className="whitespace-pre-wrap font-sans text-sm text-stone-700 bg-stone-50 border border-stone-200 rounded p-3">
        {markdown}
      </pre>
    </section>
  );
}
